/** DSN Now: active dishes per station with band tags, rate, power, range, RTLT, az/el and wind (loader-dsn.js). */
import { useState } from 'react';
import { ArrowDown, ArrowUp, Clock } from 'lucide-react';
import { clockNow } from '@/state/store';
import { Chip } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { activeDishes, dishView, simTimeDiffers, type DsnBand } from '../../../awareness/dsn';
import { useAwareness } from '../../../awareness/store';
import { Muted } from './Section';
import { useNow } from './shared';

const BAND_TONE: Record<Exclude<DsnBand, ''>, string> = {
  S: 'border-nominal/40 text-nominal',
  X: 'border-accent/40 text-accent',
  Ka: 'border-caution/40 text-caution',
};

export function DsnNowPanel() {
  const dsn = useAwareness((s) => s.dsn);
  const now = useNow(1000);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const differs = simTimeDiffers(clockNow(), now);

  const stations = dsn.data?.stations ?? [];
  const groups = stations.map((st) => ({ name: st.friendlyName || st.name || '?', dishes: activeDishes(st).map(dishView) })).filter((g) => g.dishes.length > 0);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-[10px] text-ink-3">
        {dsn.backoffMs !== null ? (
          <Chip tone="caution">feed down, retry in {Math.round(dsn.backoffMs / 1000)} s</Chip>
        ) : dsn.data ? (
          <Chip tone="live" pulse>
            live, 10 s
          </Chip>
        ) : (
          <Chip tone="neutral">connecting</Chip>
        )}
        {dsn.lastError && dsn.consecutiveFailures > 0 && <span className="truncate">{dsn.lastError}</span>}
      </div>
      {differs && (
        <div className="flex items-center gap-1.5 rounded-control border border-caution/40 bg-caution/10 px-2 py-1 text-[10px] text-caution">
          <Clock className="h-3 w-3" /> Sim time differs from real time: contact lines hidden
        </div>
      )}
      {dsn.data && groups.length === 0 && <Muted>No active dishes</Muted>}
      {groups.map((g) => (
        <div key={g.name}>
          <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-3">{g.name}</div>
          <ul className="flex flex-col gap-1">
            {g.dishes.map((d) => {
              const key = `${g.name}-${d.name}`;
              const open = Boolean(expanded[key]);
              return (
                <li key={key}>
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setExpanded((e) => ({ ...e, [key]: !open }))}
                    className="w-full rounded-control border border-hairline px-2.5 py-1.5 text-left leading-snug hover:bg-overlay"
                  >
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-mono text-ink">{d.name}</span>
                      {d.bands.map((b) => (
                        <span key={b} className={cn('rounded-full border px-1.5 text-[10px] font-medium', BAND_TONE[b as Exclude<DsnBand, ''>])}>
                          {b}
                        </span>
                      ))}
                      <span className="ml-auto truncate text-[11px]" style={{ color: d.target.color }}>
                        {d.target.name}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 font-mono text-[10px] text-ink-2">
                      {d.hasDownlink && (
                        <span title="Downlink" className="inline-flex items-center gap-0.5">
                          <ArrowDown className="h-2.5 w-2.5" /> {d.dataRateText}
                        </span>
                      )}
                      {d.hasUplink && (
                        <span title="Uplink" className="inline-flex items-center gap-0.5">
                          <ArrowUp className="h-2.5 w-2.5" />
                          {d.uplinkPowerText ? ` ${d.uplinkPowerText}` : ''}
                        </span>
                      )}
                      <span title="Range">{d.rangeText}</span>
                      <span title="Round-trip light time">RTLT {d.rtltText}</span>
                    </div>
                    {open && (
                      <div className="mt-1 flex flex-wrap gap-x-3 font-mono text-[10px] text-ink-3">
                        {d.azDeg !== null && d.elDeg !== null && <span title="Azimuth / elevation">Az {d.azDeg} deg, El {d.elDeg} deg</span>}
                        {d.windKmh !== null && <span title="Wind speed at dish">Wind {d.windKmh} km/h</span>}
                        {d.downlinkGHz.map((f, i) => (
                          <span key={`d${i}`} title="Downlink frequency">
                            down {f} GHz
                          </span>
                        ))}
                        {d.uplinkGHz.map((f, i) => (
                          <span key={`u${i}`} title="Uplink frequency">
                            up {f} GHz
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
